import React, { useState, useRef, useEffect, useCallback } from 'react';
import { CONTACT_INFO, DIRECTORY_PAGES, PROJECTS, SKILLS } from '../data/portfolio';

const DIRECTORY_NAMES = Object.keys(DIRECTORY_PAGES);

const FILESYSTEM = {
  '~': ['about.txt', 'resume.pdf', 'contact.txt', ...DIRECTORY_NAMES.map(name => `${name}/`)],
  ...Object.fromEntries(
    DIRECTORY_NAMES.map(name => [
      `~/${name}`,
      Object.keys(DIRECTORY_PAGES[name].items).map(key => `${key}.txt`),
    ])
  ),
};

const COMMANDS = [
  'help', 'ls', 'cat', 'cd', 'echo', 'whoami', 'pwd', 'clear',
  'date', 'theme', 'history', 'open', 'neofetch',
];

const ALL_FILES = [
  'about.txt', 'resume.pdf', 'contact.txt',
  ...DIRECTORY_NAMES.map(name => `${name}/`),
  ...DIRECTORY_NAMES.flatMap(name => (
    Object.keys(DIRECTORY_PAGES[name].items).map(key => `${name}/${key}.txt`)
  )),
];

const WELCOME_MESSAGE = [
  { type: 'ascii', content: `
 ┌──────────────────────────────────────────┐
 │                                          │
 │   Welcome to Sagar's Portfolio v2.0      │
 │                                          │
 │   Interactive terminal interface.        │
 │   Type 'help' to see available commands. │
 │                                          │
 └──────────────────────────────────────────┘` },
];

// ── helpers ─────────────────────────────────────────────────
function getPublicAssetPath(path) {
  const publicUrl = process.env.PUBLIC_URL || '';
  return `${publicUrl}${path.startsWith('/') ? path : `/${path}`}`;
}

function openResume() {
  window.location.assign(getPublicAssetPath('/resume.pdf'));
}

function shouldAutoFocusTerminal() {
  const hasCoarsePointer = window.matchMedia?.('(pointer: coarse)').matches;
  return !hasCoarsePointer && window.innerWidth > 700;
}

function matchTabCompletion(partial, cwd) {
  const parts = partial.split(' ');

  // Complete commands
  if (parts.length === 1) {
    const matches = COMMANDS.filter(c => c.startsWith(parts[0]));
    if (matches.length === 1) return matches[0] + ' ';
    return null;
  }

  // Complete filenames for cat, open, etc.
  if (parts.length === 2) {
    const arg = parts[1];
    let candidates = [];

    if (parts[0] === 'cat') {
      if (cwd !== '~') {
        candidates = (FILESYSTEM[cwd] || []).map(f => f);
      } else {
        candidates = ALL_FILES;
      }
    } else if (parts[0] === 'cd') {
      candidates = [...DIRECTORY_NAMES, '~', '..'];
    } else if (parts[0] === 'open') {
      candidates = ['github', 'linkedin', 'email', 'resume'];
    } else if (parts[0] === 'theme') {
      candidates = ['dark', 'light'];
    }

    const matches = candidates.filter(c => c.startsWith(arg));
    if (matches.length === 1) return parts[0] + ' ' + matches[0];
  }
  return null;
}

// ── Terminal Component ──────────────────────────────────────
export default function Terminal({ onThemeChange, isDark, onOpenFile, onOpenDirectory }) {
  const [lines, setLines] = useState(WELCOME_MESSAGE);
  const [input, setInput] = useState('');
  const [historyStack, setHistoryStack] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [cwd, setCwd] = useState('~');
  const [isTyping, setIsTyping] = useState(false);

  const inputRef = useRef(null);
  const outputRef = useRef(null);
  const terminalRef = useRef(null);

  // Auto-scroll to bottom
  useEffect(() => {
    if (outputRef.current) {
      outputRef.current.scrollTop = outputRef.current.scrollHeight;
    }
  }, [lines]);

  // Keep the terminal ready for typing without requiring a click.
  const focusInput = useCallback(() => {
    if (!isTyping && inputRef.current && shouldAutoFocusTerminal()) {
      inputRef.current.focus();
    }
  }, [isTyping]);

  useEffect(() => {
    focusInput();

    const handleWindowFocus = () => focusInput();
    const handleDocumentKeyDown = (e) => {
      const activeElement = document.activeElement;
      const isEditableElement = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable
      );

      if (!isEditableElement && !e.metaKey && !e.ctrlKey && !e.altKey) {
        focusInput();
      }
    };

    window.addEventListener('focus', handleWindowFocus);
    document.addEventListener('keydown', handleDocumentKeyDown);

    return () => {
      window.removeEventListener('focus', handleWindowFocus);
      document.removeEventListener('keydown', handleDocumentKeyDown);
    };
  }, [focusInput]);

  // ── Command execution ───────────────────────────────────
  const executeCommand = useCallback((rawCmd) => {
    const trimmed = rawCmd.trim();
    if (!trimmed) return;

    // Add to history
    setHistoryStack(prev => [...prev, trimmed]);
    setHistoryIndex(-1);

    const promptLine = { type: 'prompt', content: trimmed, cwd };
    const parts = trimmed.split(/\s+/);
    const cmd = parts[0].toLowerCase();
    const args = parts.slice(1).join(' ');

    let output = [];

    switch (cmd) {
      case 'help':
        output = [{ type: 'output', content: `
  Available commands:
  ───────────────────────────────────────
  ls                   List files in current directory
  cat <file>           View file contents
  cd <dir>             Change directory (~, projects, research, experience, hobbies)
  echo <text>          Print text to terminal
  whoami               Display current user
  pwd                  Print working directory
  clear                Clear terminal
  date                 Show current date & time
  theme <dark|light>   Switch theme
  history              Show command history
  open <target>        Open link (github, linkedin, email, resume)
  neofetch             System info
  help                 Show this help message
  ───────────────────────────────────────` }];
        break;

      case 'ls': {
        const target = args ? args.replace(/\/$/, '') : null;
        let dir = cwd;
        if (DIRECTORY_NAMES.includes(target)) dir = `~/${target}`;
        else if (target && target !== '.') {
          output = [{ type: 'error', content: `ls: cannot access '${target}': No such file or directory` }];
          break;
        }
        const files = FILESYSTEM[dir];
        if (files) {
          output = [{ type: 'filelist', content: files }];
        } else {
          output = [{ type: 'error', content: `ls: cannot access '${dir}': No such file or directory` }];
        }
        break;
      }

      case 'cat': {
        if (!args) {
          output = [{ type: 'error', content: 'cat: missing file operand' }];
          break;
        }
        let filename = args.trim();

        // Resolve relative paths
        const currentDirectory = cwd.replace('~/', '');
        if (DIRECTORY_NAMES.includes(currentDirectory) && !filename.includes('/')) {
          filename = `${currentDirectory}/${filename}`;
        }

        // Files that open in new page
        const viewableFiles = ['about.txt', 'contact.txt'];
        const isProjectFile = filename.startsWith('projects/') && PROJECTS[filename.replace('projects/', '').replace('.txt', '')];

        if (viewableFiles.includes(filename)) {
          output = [{ type: 'accent', content: `  ↗ Opening ${filename}...` }];
          setTimeout(() => onOpenFile(filename), 150);
        } else if (filename === 'resume.pdf') {
          output = [{ type: 'accent', content: '  ↗ Opening resume...' }];
          openResume();
        } else if (isProjectFile) {
          output = [{ type: 'accent', content: `  ↗ Opening ${filename}...` }];
          setTimeout(() => onOpenFile(filename), 150);
        } else if (DIRECTORY_NAMES.some(name => filename.startsWith(`${name}/`))) {
          const [directory, file] = filename.split('/');
          const itemKey = file.replace('.txt', '');
          output = [{ type: 'output', content: `  ${DIRECTORY_PAGES[directory]?.items[itemKey]?.description || 'Open this folder with cd to view the full page.'}` }];
        } else {
          output = [{ type: 'error', content: `cat: ${filename}: No such file or directory` }];
        }
        break;
      }

      case 'cd': {
        if (!args || args === '~') {
          setCwd('~');
          output = [];
        } else if (DIRECTORY_NAMES.includes(args.replace(/\/$/, ''))) {
          const directory = args.replace(/\/$/, '');
          setCwd(`~/${directory}`);
          output = [{ type: 'accent', content: `  ↗ Opening ${directory}...` }];
          onOpenDirectory(directory);
        } else if (args === '..' || args === '../') {
          setCwd('~');
          output = [];
        } else {
          output = [{ type: 'error', content: `cd: ${args}: No such file or directory` }];
        }
        break;
      }

      case 'echo':
        output = [{ type: 'output', content: `  ${args}` }];
        break;

      case 'whoami':
        output = [{ type: 'output', content: '  sagar' }];
        break;

      case 'pwd':
        output = [{ type: 'output', content: `  ${cwd === '~' ? '~/portfolio' : cwd}` }];
        break;

      case 'clear':
        setLines([]);
        return;

      case 'date':
        output = [{ type: 'output', content: `  ${new Date().toString()}` }];
        break;

      case 'theme':
        if (args === 'dark') {
          onThemeChange(true);
          output = [{ type: 'accent', content: '  ✓ Switched to dark mode' }];
        } else if (args === 'light') {
          onThemeChange(false);
          output = [{ type: 'accent', content: '  ✓ Switched to light mode' }];
        } else {
          output = [{ type: 'output', content: `  Current theme: ${isDark ? 'dark' : 'light'}\n  Usage: theme <dark|light>` }];
        }
        break;

      case 'history':
        if (historyStack.length === 0) {
          output = [{ type: 'output', content: '  No commands in history.' }];
        } else {
          const hist = historyStack.map((h, i) => `  ${String(i + 1).padStart(4)}  ${h}`).join('\n');
          output = [{ type: 'output', content: hist }];
        }
        break;

      case 'open': {
        const target = args.toLowerCase();
        if (target === 'github') {
          output = [{ type: 'accent', content: '  ↗ Opening GitHub...' }];
          window.open(`https://${CONTACT_INFO.github}`, '_blank');
        } else if (target === 'linkedin') {
          output = [{ type: 'accent', content: '  ↗ Opening LinkedIn...' }];
          window.open(`https://${CONTACT_INFO.linkedin}`, '_blank');
        } else if (target === 'email') {
          output = [{ type: 'accent', content: '  ↗ Opening email client...' }];
          window.open(`mailto:${CONTACT_INFO.email}`, '_blank');
        } else if (target === 'resume') {
          output = [{ type: 'accent', content: '  ↗ Opening resume...' }];
          openResume();
        } else {
          output = [{ type: 'error', content: `  open: unknown target '${target}'. Options: github, linkedin, email, resume` }];
        }
        break;
      }

      case 'neofetch':
        output = [{ type: 'output', content: `
       ╭──────╮          sagar@portfolio
       │  S_  │          ──────────────────
       ╰──────╯          OS:     React 19 × Three.js
                          Shell:  portfolio-cli v2.0
    ▄▄▄▄▄▄▄▄▄▄          Theme:  ${isDark ? 'dark' : 'light'}
    █ ▀▀▀▀▀▀ █          Tech:   ${SKILLS.slice(0, 4).join(', ')}
    █ ██  ██ █                  ${SKILLS.slice(4).join(', ')}
    █  ════  █          ──────────────────
    ▀▀▀▀▀▀▀▀▀▀          ■ ■ ■ ■ ■ ■ ■ ■` }];
        break;

      case 'sudo':
        output = [{ type: 'error', content: '  Nice try. 😏' }];
        break;

      case 'rm':
        output = [{ type: 'error', content: '  Permission denied. This portfolio is protected. 🛡️' }];
        break;

      case 'exit':
        output = [{ type: 'output', content: '  There is no escape. You must stay and explore. 🚀' }];
        break;

      default:
        output = [{ type: 'error', content: `  command not found: ${cmd}. Type 'help' for available commands.` }];
    }

    // Animate output
    setLines(prev => [...prev, promptLine]);
    setIsTyping(true);

    // Add output lines with a small stagger
    if (output.length > 0) {
      setTimeout(() => {
        setLines(prev => [...prev, ...output]);
        setIsTyping(false);
      }, 80);
    } else {
      setIsTyping(false);
    }
  }, [cwd, historyStack, isDark, onThemeChange, onOpenFile, onOpenDirectory]);

  // ── Key handler ─────────────────────────────────────────
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      executeCommand(input);
      setInput('');
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHistoryStack(prev => {
        if (prev.length === 0) return prev;
        const newIdx = historyIndex === -1 ? prev.length - 1 : Math.max(0, historyIndex - 1);
        setHistoryIndex(newIdx);
        setInput(prev[newIdx]);
        return prev;
      });
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHistoryStack(prev => {
        if (prev.length === 0) return prev;
        if (historyIndex === -1) return prev;
        const newIdx = historyIndex + 1;
        if (newIdx >= prev.length) {
          setHistoryIndex(-1);
          setInput('');
        } else {
          setHistoryIndex(newIdx);
          setInput(prev[newIdx]);
        }
        return prev;
      });
    } else if (e.key === 'Tab') {
      e.preventDefault();
      const completed = matchTabCompletion(input, cwd);
      if (completed) setInput(completed);
    } else if (e.key === 'l' && e.ctrlKey) {
      e.preventDefault();
      setLines([]);
    }
  }, [input, historyIndex, executeCommand, cwd]);

  // ── Render line ─────────────────────────────────────────
  const renderLine = (line, i) => {
    switch (line.type) {
      case 'prompt':
        return (
          <div key={i} className="terminal-line terminal-line-prompt">
            <span className="terminal-prompt-user">sagar</span>
            <span className="terminal-prompt-at">@</span>
            <span className="terminal-prompt-host">portfolio</span>
            <span className="terminal-prompt-separator">:</span>
            <span className="terminal-prompt-path">{line.cwd}</span>
            <span className="terminal-prompt-dollar">$</span>
            <span className="terminal-prompt-cmd">{line.content}</span>
          </div>
        );
      case 'output':
        return (
          <div key={i} className="terminal-line terminal-line-output">
            <pre>{line.content}</pre>
          </div>
        );
      case 'error':
        return (
          <div key={i} className="terminal-line terminal-line-error">
            <pre>{line.content}</pre>
          </div>
        );
      case 'accent':
        return (
          <div key={i} className="terminal-line terminal-line-accent">
            <pre>{line.content}</pre>
          </div>
        );
      case 'ascii':
        return (
          <div key={i} className="terminal-line terminal-line-ascii">
            <pre>{line.content}</pre>
          </div>
        );
      case 'filelist':
        return (
          <div key={i} className="terminal-line terminal-line-filelist">
            {line.content.map((file, j) => (
              <span
                key={j}
                className={`terminal-file ${file.endsWith('/') ? 'terminal-file-dir' : ''}`}
              >
                {file}
              </span>
            ))}
          </div>
        );
      default:
        return (
          <div key={i} className="terminal-line">
            <pre>{line.content}</pre>
          </div>
        );
    }
  };

  return (
    <div className="terminal" ref={terminalRef} onClick={focusInput}>
      <div className="terminal-titlebar">
        <div className="terminal-dots">
          <span className="terminal-dot terminal-dot-red" />
          <span className="terminal-dot terminal-dot-yellow" />
          <span className="terminal-dot terminal-dot-green" />
        </div>
        <span className="terminal-title">sagar — portfolio-cli</span>
        <div className="terminal-dots-spacer" />
      </div>

      <div className="terminal-body" ref={outputRef}>
        {lines.map(renderLine)}

        <div className="terminal-input-line">
          <span className="terminal-prompt-user">sagar</span>
          <span className="terminal-prompt-at">@</span>
          <span className="terminal-prompt-host">portfolio</span>
          <span className="terminal-prompt-separator">:</span>
          <span className="terminal-prompt-path">{cwd}</span>
          <span className="terminal-prompt-dollar">$</span>
          <input
            ref={inputRef}
            type="text"
            className="terminal-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => setTimeout(focusInput, 0)}
            spellCheck={false}
            autoComplete="off"
            autoFocus={shouldAutoFocusTerminal()}
            disabled={isTyping}
          />
        </div>
      </div>
    </div>
  );
}
